import { IconColors, Warning } from 'Icons';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'Shared/Button';
import { ButtonRow } from 'Shared/ButtonRow';
import formStyles from 'Shared/Forms/forms.module.css';
import Modal from 'Shared/Modal/Modal';

import styles from './danger-confirmation-modal.module.css';

interface DangerConfirmationModalProps {
    header: string;
    /** What the action destroys. Rendered under the irreversible-action banner. */
    children: React.ReactNode;
    /** The user has to type this exactly before the action button unlocks. */
    confirmationPhrase: string;
    /** Sentence introducing the input, naming the phrase to type. */
    typePrompt: React.ReactNode;
    /** Accessible name for the input, since the prompt above it is not a plain string. */
    inputLabel: string;
    confirmLabel: string;
    onCancel: () => void;
    onConfirm: () => void;
    isBusy?: boolean;
}

/**
 * Confirmation for actions that destroy data for good. Deliberately harder to get
 * through than `ConfirmationModal`.
 * The consequences are spelled out and the action stays locked until the user types
 * the name of what they are about to destroy.
 */
function DangerConfirmationModal({
    header,
    children,
    confirmationPhrase,
    typePrompt,
    inputLabel,
    confirmLabel,
    onCancel,
    onConfirm,
    isBusy = false,
}: DangerConfirmationModalProps): React.ReactElement {
    const { t } = useTranslation(['buttons', 'common']);
    const inputId = React.useId();

    const [typedPhrase, setTypedPhrase] = React.useState('');

    const isConfirmed = typedPhrase.trim() === confirmationPhrase.trim();

    // Rendered at the body so the overlay never inherits type styles from whatever it was opened from
    // Callers mount it from places like table rows, whose stylesheets restyle every descendant paragraph
    return createPortal(
        <Modal header={header} callBack={onCancel} style={{ maxWidth: '34rem' }}>
            <div className={styles.Content} data-qa="danger-confirmation-modal">
                <p className={styles.Banner}>
                    <Warning aspect="1.125rem" color={IconColors.WarningRed} />
                    <span>{t('common:thisActionCannotBeUndone')}</span>
                </p>

                {children}

                <div className={styles.ConfirmField}>
                    <label className={styles.TypePrompt} htmlFor={inputId}>
                        {typePrompt}
                    </label>
                    <input
                        id={inputId}
                        type="text"
                        className={formStyles.InputElement}
                        value={typedPhrase}
                        onChange={(event) => setTypedPhrase(event.target.value)}
                        placeholder={confirmationPhrase}
                        autoComplete="off"
                        aria-label={inputLabel}
                        data-qa="danger-confirmation-input"
                        disabled={isBusy}
                    />
                </div>
            </div>

            <ButtonRow withMarginBottom style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
                <Button inverse text={t('buttons:cancel')} callback={onCancel} disable={isBusy} />
                <Button warning text={confirmLabel} callback={onConfirm} disable={!isConfirmed || isBusy} />
            </ButtonRow>
        </Modal>,
        document.body,
    );
}

export default DangerConfirmationModal;
